/**
 * IonRailButton — the collapsed rail's launcher button, split out of
 * IonSurface (S3 fix-wave review, Fix 3/4/5/6) so the heartbeat's signal
 * reads (chatStreaming, commandProvenance, filteredCells, allCells) and its
 * steering-decay interval only re-render THIS leaf. IonSurface mounts once
 * for the app's entire lifetime, so before this split any of those signal
 * writes — e.g. every filter/slider step on /explorer, which replaces
 * `filteredCells` with a new array reference — re-rendered the whole
 * always-mounted Ion subtree (header, usage meter, message list, input,
 * history drawer, and in `full` the artifact canvas), none of which are
 * memoized.
 *
 * Props are exactly what the parent already holds and that do NOT change on
 * every filter tick (minimized/surface/pathname/hasUnseen/onClick) — the
 * heartbeat-specific signals are read here, not passed down.
 */

import { useEffect, useState } from 'preact/hooks'
import { chatStreaming } from '../../state/chatStore.js'
import { commandProvenance } from '../../state/commandLayer.js'
import { filteredCells, allCells } from '../../state/globalSignals.js'
import { railActivityStatus, railIdleContext, isRailContextRoute } from './ionRailHeartbeat.js'
import { IconIonBolt } from '../shared/Icons.jsx'
import { t } from '../../i18n/index.js'

/**
 * @param {object} props
 * @param {boolean} props.minimized
 * @param {'rail'|'docked'|'full'} props.surface
 * @param {string} props.pathname — current route, for the idle context hint
 * @param {boolean} props.hasUnseen
 * @param {() => void} props.onClick
 */
export function IonRailButton({ minimized, surface, pathname, hasUnseen, onClick }) {
  // Active: chatStreaming + the last commandProvenance decide "steering…"
  // (Ion just drove an explorer-control tool) vs. the generic "working…".
  const streaming = chatStreaming.value
  const provenance = commandProvenance.value
  const railStatus = railActivityStatus(streaming, provenance, Date.now())

  // Re-render periodically WHILE a "steering…" cue could still be alive so
  // it decays back to "working…" a few seconds after Ion goes quiet
  // (composing a text reply) without waiting on the next signal write to
  // trigger it. Gated to exactly the case this is needed for:
  //   - railStatus === 'steering' — nothing to decay from 'working' or idle,
  //     so the effect (and its dependency) clears itself the moment
  //     railActivityStatus flips, bounding the ticking to at most
  //     STEERING_WINDOW_MS regardless of how long the stream runs.
  //   - surface === 'rail' — docked/full hide this button entirely via CSS,
  //     so ticking there could never even be observed; also keeps the
  //     interval from running on every other route/geometry.
  const [, forceRailTick] = useState(0)
  useEffect(() => {
    if (railStatus !== 'steering' || surface !== 'rail') return undefined
    const id = setInterval(() => forceRailTick((n) => n + 1), 500)
    return () => clearInterval(id)
  }, [railStatus, surface])

  // Idle: a compact context hint for the current surface, surfaced as the
  // rail's `title` tooltip only — Explorer's "Cell Explorer · N matches" is
  // the one case this design specs; every other route stays null (plain
  // "Ask Ion", no tooltip). Route-gated via isRailContextRoute BEFORE the
  // filteredCells/allCells reads, so a non-Explorer route never subscribes
  // to either signal.
  // This button renders ABOVE ChatErrorBoundary in IonSurface's tree (the
  // boundary only wraps the panel content further down), so a signal write
  // that isn't an array here would white the whole AppShell, not just the
  // chat. Nothing writes anything but arrays today, but the codebase is
  // defensive at comparable read sites — optional-chain + fall back to 0.
  const onContextRoute = isRailContextRoute(pathname)
  const railHint =
    !railStatus && onContextRoute
      ? railIdleContext(pathname, filteredCells.value?.length ?? 0, (allCells.value?.length ?? 0) > 0)
      : null
  const railTitle = railHint ? t(railHint.key, railHint.params) : undefined

  // Accessible name carries the live state too — `title` is NOT announced
  // when `aria-label` is present (aria-label always wins for the accessible
  // name), and the visible status word is aria-hidden, so without this the
  // busy/idle-hint state would be invisible to assistive tech.
  //
  // Both states APPEND to the base label rather than replacing it: this is a
  // button, and its accessible name has to keep describing the action it
  // performs. An accessible name of just "Ion is working…" tells a screen-
  // reader user the state but no longer tells them the control opens the
  // chat.
  const baseAriaLabel = minimized ? t('ion.launcher.active.aria') : t('ion.launcher.aria')
  const ariaContext = railStatus
    ? t(
        railStatus === 'steering'
          ? 'ion.launcher.aria.busy.steering'
          : 'ion.launcher.aria.busy.working',
      )
    : railTitle
  const ariaLabel = ariaContext
    ? t('ion.launcher.aria.withContext', { label: baseAriaLabel, context: ariaContext })
    : baseAriaLabel

  // Only the true desktop rail geometry ever shows this visually (docked/
  // full hide the button via CSS) — gate the busy class on it too instead of
  // setting it geometry-blind, so it's never true for a button that isn't
  // actually the rail.
  const busy = railStatus && surface === 'rail'

  return (
    <button
      type="button"
      class={`ion-rail${minimized ? ' ion-rail--active' : ''}${busy ? ' ion-rail--busy' : ''}`}
      aria-label={ariaLabel}
      title={railTitle}
      onClick={onClick}
    >
      <IconIonBolt size={22} className="ion-rail__bolt" animated />
      <span class="ion-rail__pulse" aria-hidden="true" />
      <span class="ion-rail__label">{t('ion.launcher.label')}</span>
      {/* Idle page context as a second, quieter segment — the number only, with
          the full sentence left in the `title` tooltip. Rail v2 surfaced this
          vertically down the strip; v3 keeps the signal (it is the part that
          makes the launcher context-aware rather than a bolted-on widget) while
          staying one short horizontal line. `aria-hidden` because the accessible
          name already carries the same context via `ariaLabel` — announcing it
          twice is worse than not at all. */}
      {railHint && !railStatus && (
        <span class="ion-rail__context" aria-hidden="true">
          · {railHint.params.count}
        </span>
      )}
      {railStatus && (
        <span class="ion-rail__status" aria-hidden="true">
          {t(railStatus === 'steering' ? 'ion.rail.status.steering' : 'ion.rail.status.working')}
        </span>
      )}
      {hasUnseen && <span class="ion-rail__badge" aria-hidden="true" />}
    </button>
  )
}
